import { useState } from 'react'

import {
  type Availability,
  type ExtractedBlock,
  type ExtractionFailure,
  type MaterialSubject,
} from '@/platform/materials/extraction'

import { formatSize } from './file'
import { JourneyFrame, Note } from './JourneyChrome'
import { type LocalMaterial } from './MaterialEntry'
import {
  canConfirm,
  type ConfirmedMaterial,
  pendingBlocks,
  type Resolution,
  resolutionOf,
  type ReviewState,
} from './review'
import {
  CONFIRM_MEANING_TEXT,
  FAILURE_TEXT,
  KIND_TEXT,
  NEXT_STEP_TEXT,
  NOT_LEARNING_SENT_TEXT,
  OTHER_SUBJECT_TEXT,
  REASON_TEXT,
  RESTRICTED_PARENT_TEXT,
  restrictedText,
  RETRYABLE,
  SEND_TEXT,
  SUBJECT_NAME,
  SYNTHETIC_FIXTURE_NOTICE_TEXT,
  UNCERTAIN_MATERIAL_TEXT,
  unavailableText,
} from './read-text'
import { sendableSubject, type SubjectChoice } from './subject'
import { PANEL, PRIMARY, SECONDARY } from './ui'

/**
 * Slice 3: reading a real Mathematics document. Nothing here is DEMO or
 * simulated: the text comes from the `extract-material` function and its
 * configured provider, and is shown as read, with what is uncertain or
 * unreadable marked and explained. Only content the learner has checked
 * against the original can be confirmed.
 */

// ---------------------------------------------------------------------------
// File preview: send for reading, or say honestly that reading is not available
// ---------------------------------------------------------------------------

/** Reasons that can pass on their own or after signing in again: offer "Thử lại". */
const RECHECKABLE: ReadonlySet<string> = new Set([
  'unreachable',
  'not_authenticated',
  'provider_failed',
  'invalid_response',
])

export type ReaderState = { kind: 'checking' } | Availability

/**
 * Upload is closed for this learner (entitlement off, or restricted): the
 * entry screen shows this instead of the file pickers, and points to the
 * lessons already in the app.
 */
export function UploadClosed({
  reader,
  onLearn,
}: {
  reader: Extract<Availability, { available: false }>
  onLearn: () => void
}): React.JSX.Element {
  return (
    <section
      aria-labelledby="upload-closed"
      className="flex flex-col gap-3 rounded-3xl border-2 border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30"
    >
      <h2 id="upload-closed" className="flex items-center gap-2 text-lg font-semibold">
        <span aria-hidden>🔒</span> Chưa gửi được tài liệu
      </h2>
      <p className="text-base">{unavailableText(reader)}</p>
      {reader.why === 'restricted' && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          {RESTRICTED_PARENT_TEXT}
        </p>
      )}
      <button
        type="button"
        className={`${PRIMARY} self-stretch md:self-start`}
        onClick={onLearn}
      >
        Học bài có sẵn
      </button>
    </section>
  )
}

const SUBJECT_LABEL: Readonly<Record<MaterialSubject, string>> = {
  mathematics: 'Toán',
  english: 'Tiếng Anh',
}

export function SendForReading({
  reader,
  cancelled,
  samplesAvailable,
  onSend,
  onRecheck,
  onSamples,
  subject,
  onSubject,
}: {
  reader: ReaderState
  /** The last reading was stopped by the learner. */
  cancelled: boolean
  samplesAvailable: boolean
  onSend: () => void
  onRecheck: () => void
  onSamples: () => void
  /** The subject the learner chose for this material; none until they choose. */
  subject: SubjectChoice | null
  onSubject: (subject: SubjectChoice) => void
}): React.JSX.Element {
  if ('kind' in reader)
    return (
      <p role="status" className="text-lg text-slate-700 dark:text-slate-300">
        Đang kiểm tra chức năng đọc tài liệu…
      </p>
    )

  if (reader.available) {
    // Never a default subject: material is sent only as a subject the learner
    // chose and the server offers (D-21; the server checks again).
    const offered = reader.subjects ?? ['mathematics']
    const choices: readonly { value: SubjectChoice; label: string }[] = [
      ...offered.map((s) => ({
        value: s,
        label:
          reader.syntheticFixtureSubjects?.includes(s) === true
            ? `${SUBJECT_LABEL[s]} (bản thử trên máy phát triển)`
            : SUBJECT_LABEL[s],
      })),
      {
        value: 'other',
        label: offered.includes('english')
          ? 'Môn khác'
          : 'Môn khác (Tiếng Anh, Vật lý, Hóa học…)',
      },
    ]
    const sendable = sendableSubject(reader, subject)
    return (
      <section aria-labelledby="send-for-reading" className={PANEL}>
        <h2
          id="send-for-reading"
          className="flex items-center gap-2 text-lg font-semibold"
        >
          <span aria-hidden>🔎</span> Đọc nội dung tài liệu
        </h2>
        {cancelled && (
          <p role="status" className="text-base font-semibold">
            Đã dừng. Tài liệu chưa được đọc.
          </p>
        )}
        <fieldset className="flex flex-col gap-2">
          <legend className="text-base font-semibold">Tài liệu này là môn nào?</legend>
          <div className="flex flex-wrap gap-2">
            {choices.map((c) => (
              <label
                key={c.value}
                className="flex min-h-12 items-center gap-2 rounded-2xl border-2 border-slate-300 px-4 text-lg dark:border-slate-700"
              >
                <input
                  type="radio"
                  name="material-subject"
                  value={c.value}
                  checked={subject === c.value}
                  onChange={() => {
                    onSubject(c.value)
                  }}
                  className="size-5"
                />
                {c.label}
              </label>
            ))}
          </div>
        </fieldset>
        {subject === 'other' && <Note tone="info">{OTHER_SUBJECT_TEXT}</Note>}
        <div className="flex flex-col gap-2 rounded-2xl bg-sky-50 p-3 text-base dark:bg-sky-950/40">
          <p className="font-semibold">Tệp sẽ được gửi đi đâu?</p>
          {/* The server, not the subject name, says which subjects only the fixture serves. */}
          {sendable !== null &&
          reader.syntheticFixtureSubjects?.includes(sendable) === true ? (
            SYNTHETIC_FIXTURE_NOTICE_TEXT.map((line) => <p key={line}>{line}</p>)
          ) : (
            <>
              <p>
                Khi con bấm “{SEND_TEXT}”, tệp được gửi tới máy chủ của ứng dụng, rồi tới
                dịch vụ đọc tài liệu{' '}
                <span className="font-semibold">{reader.provider.name}</span> để đọc chữ.
                Ứng dụng không lưu tệp này.
              </p>
              <p>{reader.notice}</p>
            </>
          )}
          <p>Nếu chưa chắc có nên gửi không, con hỏi bố mẹ trước nhé.</p>
        </div>
        {subject === null && (
          <p id="choose-subject" className="text-base">
            Con chọn môn của tài liệu trước nhé.
          </p>
        )}
        <button
          type="button"
          className={`${PRIMARY} self-stretch md:self-start`}
          disabled={sendable === null}
          aria-describedby={subject === null ? 'choose-subject' : undefined}
          onClick={onSend}
        >
          {SEND_TEXT}
        </button>
      </section>
    )
  }

  return (
    <section
      aria-labelledby="not-connected"
      className="flex flex-col gap-2 rounded-3xl border-2 border-dashed border-amber-300 p-4 dark:border-amber-800"
    >
      <h2 id="not-connected" className="flex items-center gap-2 text-lg font-semibold">
        <span aria-hidden>🔌</span> Chưa đọc được nội dung
      </h2>
      <p className="text-base">{unavailableText(reader)}</p>
      {RECHECKABLE.has(reader.why) && (
        <button type="button" className={`${SECONDARY} self-start`} onClick={onRecheck}>
          Thử lại
        </button>
      )}
      <p className="text-base text-slate-700 dark:text-slate-300">
        {samplesAvailable
          ? 'Con có thể thử tài liệu mẫu để xem cả hành trình học.'
          : 'Khi chức năng này có, con sẽ học tiếp được từ đây.'}
      </p>
      {samplesAvailable && (
        <button
          type="button"
          className={`${PRIMARY} self-stretch md:self-start`}
          onClick={onSamples}
        >
          Thử tài liệu mẫu
        </button>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Reading in progress / failed
// ---------------------------------------------------------------------------

export function ReadingProgress({
  material,
  onCancel,
  onHome,
}: {
  material: LocalMaterial
  onCancel: () => void
  onHome: () => void
}): React.JSX.Element {
  return (
    <JourneyFrame
      step="Nội dung"
      title="Đang đọc tài liệu"
      onBack={onCancel}
      onHome={onHome}
    >
      <div role="status" className={`${PANEL} items-center text-center`}>
        <span
          aria-hidden
          className="size-12 animate-spin rounded-full border-4 border-sky-200 border-t-sky-700 motion-reduce:animate-none dark:border-sky-900 dark:border-t-sky-300"
        />
        <p className="text-lg font-semibold">Ứng dụng đang đọc “{material.file.name}”…</p>
        <p className="text-base text-slate-700 dark:text-slate-300">
          Việc này có thể mất tới một phút với tài liệu nhiều trang.
        </p>
      </div>
      <button type="button" className={SECONDARY} onClick={onCancel}>
        Dừng lại
      </button>
    </JourneyFrame>
  )
}

export function ReadingFailed({
  failure,
  restrictedUntil,
  onRetry,
  onChooseAnother,
  onHome,
}: {
  failure: Exclude<ExtractionFailure, 'cancelled'>
  /** Set when uploading is now restricted for this learner. */
  restrictedUntil: string | null
  onRetry: () => void
  onChooseAnother: () => void
  onHome: () => void
}): React.JSX.Element {
  const retryable = RETRYABLE.has(failure)
  const notLearning = failure === 'not_learning_material'
  const closed =
    failure === 'restricted' ||
    failure === 'not_entitled' ||
    (notLearning && restrictedUntil !== null)
  const restricted = failure === 'restricted' || (notLearning && restrictedUntil !== null)
  return (
    <JourneyFrame
      step="Nội dung"
      title={notLearning ? 'Đây không phải tài liệu học' : 'Chưa đọc được tài liệu'}
      onBack={onChooseAnother}
      onHome={onHome}
    >
      <p
        role="alert"
        className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
      >
        {failure === 'restricted'
          ? restrictedText(restrictedUntil)
          : FAILURE_TEXT[failure]}
      </p>
      {notLearning && <Note tone="info">{NOT_LEARNING_SENT_TEXT}</Note>}
      {notLearning && restrictedUntil !== null && (
        <p className="text-lg font-semibold">{restrictedText(restrictedUntil)}</p>
      )}
      {restricted && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          {RESTRICTED_PARENT_TEXT}
        </p>
      )}
      <div className="flex flex-col gap-3 md:flex-row">
        {retryable && (
          <button type="button" className={PRIMARY} onClick={onRetry}>
            Thử lại
          </button>
        )}
        {closed ? (
          <button type="button" className={PRIMARY} onClick={onHome}>
            Học bài có sẵn
          </button>
        ) : (
          <button
            type="button"
            className={retryable ? SECONDARY : PRIMARY}
            onClick={onChooseAnother}
          >
            Chọn tài liệu khác
          </button>
        )}
      </div>
    </JourneyFrame>
  )
}

// ---------------------------------------------------------------------------
// Review: original next to what was read
// ---------------------------------------------------------------------------

/** The text as read, with doubtful words marked. */
function ReadText({
  block,
  lang,
}: {
  block: ExtractedBlock
  lang: string | undefined
}): React.JSX.Element {
  if (block.text.trim() === '')
    return (
      <p className="text-lg text-slate-600 italic dark:text-slate-400">
        (không đọc được chữ)
      </p>
    )
  const parts: React.ReactNode[] = []
  let at = 0
  block.uncertainSpans.forEach((span, i) => {
    parts.push(block.text.slice(at, span.start))
    parts.push(
      <mark
        key={i}
        className="rounded bg-amber-200 px-0.5 text-slate-950 underline decoration-amber-700 decoration-wavy dark:bg-amber-700/60 dark:text-white"
      >
        {block.text.slice(span.start, span.end)}
        <span className="sr-only"> (chưa chắc)</span>
      </mark>,
    )
    at = span.end
  })
  parts.push(block.text.slice(at))
  return (
    <p lang={lang} className="text-lg break-words whitespace-pre-wrap">
      {parts}
    </p>
  )
}

const STATUS_BADGE: Readonly<
  Record<ExtractedBlock['status'], { text: string; className: string }>
> = {
  clear: {
    text: 'Đọc rõ',
    className:
      'bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100',
  },
  uncertain: {
    text: '⚠️ Chưa chắc chắn',
    className: 'bg-amber-100 text-amber-950 dark:bg-amber-950 dark:text-amber-100',
  },
  unreadable: {
    text: '⛔ Không đọc được',
    className: 'bg-rose-100 text-rose-950 dark:bg-rose-950 dark:text-rose-100',
  },
}

function ResolutionNote({
  resolution,
}: {
  resolution: Resolution
}): React.JSX.Element | null {
  switch (resolution.kind) {
    case 'accepted':
      return null
    case 'pending':
      return (
        <p className="text-base font-semibold text-amber-900 dark:text-amber-200">
          Con cần kiểm tra chỗ này với tài liệu gốc.
        </p>
      )
    case 'corrected':
      return (
        <div className="rounded-xl bg-sky-50 p-2 dark:bg-sky-950/40">
          <p className="text-sm font-semibold text-sky-900 dark:text-sky-200">
            Con đã sửa thành:
          </p>
          <p className="text-lg break-words whitespace-pre-wrap">{resolution.text}</p>
        </div>
      )
    case 'excluded':
      return <p className="text-base font-semibold">Đã bỏ phần này: không dùng để học.</p>
  }
}

function BlockCard({
  block,
  resolution,
  highlighted,
  canLocate,
  onLocate,
  onResolve,
  lang,
}: {
  block: ExtractedBlock
  lang: string | undefined
  resolution: Resolution
  highlighted: boolean
  canLocate: boolean
  onLocate: () => void
  onResolve: (resolution: Resolution) => void
}): React.JSX.Element {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const badge = STATUS_BADGE[block.status]
  const labelId = `block-${block.id}`
  const unreadable = block.status === 'unreadable' || block.text.trim() === ''
  const resolved = resolution.kind !== 'pending'
  const accepted = resolution.kind === 'accepted' && block.status !== 'clear'

  const startEdit = () => {
    setDraft(resolution.kind === 'corrected' ? resolution.text : block.text)
    setEditing(true)
  }

  return (
    <li
      aria-labelledby={labelId}
      className={`flex flex-col gap-2 rounded-2xl border-2 p-3 ${
        highlighted ? 'ring-4 ring-sky-400' : ''
      } ${
        resolution.kind === 'pending'
          ? block.status === 'unreadable'
            ? 'border-rose-400 bg-rose-50 dark:border-rose-700 dark:bg-rose-950/30'
            : 'border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30'
          : resolution.kind === 'excluded'
            ? 'border-slate-200 bg-slate-100 opacity-80 dark:border-slate-800 dark:bg-slate-800'
            : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span id={labelId} className="font-semibold text-slate-700 dark:text-slate-300">
          {KIND_TEXT[block.kind]} · Trang {block.page}
        </span>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${badge.className}`}>
          {badge.text}
        </span>
        {accepted && (
          <span className="rounded-full bg-sky-100 px-2 py-0.5 font-semibold text-sky-950 dark:bg-sky-950 dark:text-sky-100">
            ✓ Con đã kiểm tra
          </span>
        )}
      </div>
      <ReadText block={block} lang={lang} />
      {block.reason !== null && (
        <p className="text-base text-slate-800 dark:text-slate-200">
          {REASON_TEXT[block.reason]}
        </p>
      )}
      <ResolutionNote resolution={resolution} />

      {editing ? (
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-base font-semibold">
            Gõ lại đúng như trong tài liệu gốc
            <textarea
              value={draft}
              rows={3}
              lang={lang}
              spellCheck={false}
              onChange={(e) => {
                setDraft(e.currentTarget.value)
              }}
              className="rounded-xl border-2 border-slate-300 bg-white p-2 text-lg font-normal dark:border-slate-700 dark:bg-slate-950"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={SECONDARY}
              disabled={draft.trim() === ''}
              onClick={() => {
                onResolve({ kind: 'corrected', text: draft })
                setEditing(false)
              }}
            >
              Lưu
            </button>
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                setEditing(false)
              }}
            >
              Hủy
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {canLocate && (
            <button
              type="button"
              className="min-h-11 px-1 text-base text-sky-800 underline dark:text-sky-300"
              onClick={onLocate}
            >
              Xem chỗ này trên ảnh
            </button>
          )}
          {resolution.kind === 'pending' && !unreadable && (
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                onResolve({ kind: 'accepted' })
              }}
            >
              Đúng như tài liệu
            </button>
          )}
          {resolution.kind !== 'excluded' && (
            <button type="button" className={SECONDARY} onClick={startEdit}>
              {unreadable ? 'Gõ lại' : 'Sửa'}
            </button>
          )}
          {resolution.kind !== 'excluded' && (
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                onResolve({ kind: 'excluded' })
              }}
            >
              Bỏ phần này
            </button>
          )}
          {resolved && block.status !== 'clear' && (
            <button
              type="button"
              className="min-h-11 px-1 text-base text-slate-700 underline dark:text-slate-300"
              onClick={() => {
                onResolve({ kind: 'pending' })
              }}
            >
              Kiểm tra lại
            </button>
          )}
          {resolution.kind === 'excluded' && block.status === 'clear' && (
            <button
              type="button"
              className={SECONDARY}
              onClick={() => {
                onResolve({ kind: 'accepted' })
              }}
            >
              Giữ lại
            </button>
          )}
        </div>
      )}
    </li>
  )
}

/** The original, with the regions the provider reported drawn on photos. */
export function Original({
  material,
  blocks,
  highlightedId,
}: {
  material: LocalMaterial
  blocks: readonly ExtractedBlock[]
  highlightedId: string | null
}): React.JSX.Element {
  const [broken, setBroken] = useState(false)
  return (
    <section
      aria-labelledby="real-original"
      className={`${PANEL} md:sticky md:top-2 md:self-start`}
    >
      <h2 id="real-original" className="text-lg font-semibold">
        <span aria-hidden>📄 </span>Tài liệu gốc
      </h2>
      <p
        className="truncate text-base text-slate-600 dark:text-slate-400"
        title={material.file.name}
      >
        {material.file.name} · {formatSize(material.file.size)}
      </p>
      {material.kind === 'image' && !broken && (
        <div className="relative mx-auto w-fit max-w-full">
          <img
            src={material.url}
            alt={`Tài liệu gốc: ${material.file.name}`}
            className="h-auto max-h-[70dvh] w-auto max-w-full rounded-xl object-contain"
            onError={() => {
              setBroken(true)
            }}
          />
          {blocks.map((b) =>
            b.region === null ||
            (b.status === 'clear' && b.id !== highlightedId) ? null : (
              <span
                key={b.id}
                aria-hidden
                data-testid={`region-${b.id}`}
                className={`pointer-events-none absolute rounded border-2 ${
                  b.id === highlightedId
                    ? 'border-sky-600 bg-sky-400/25'
                    : b.status === 'unreadable'
                      ? 'border-rose-600 bg-rose-400/15'
                      : 'border-amber-500 bg-amber-300/20'
                }`}
                style={{
                  left: `${String(b.region.x * 100)}%`,
                  top: `${String(b.region.y * 100)}%`,
                  width: `${String(b.region.width * 100)}%`,
                  height: `${String(b.region.height * 100)}%`,
                }}
              />
            ),
          )}
        </div>
      )}
      {material.kind === 'image' && broken && (
        <p
          role="alert"
          className="rounded-xl bg-slate-100 p-4 text-center dark:bg-slate-800"
        >
          Trình duyệt không hiển thị được ảnh này. Con mở ảnh cỡ thật để so sánh nhé.
        </p>
      )}
      {material.kind === 'pdf' && (
        <object
          data={material.url}
          type="application/pdf"
          aria-label={`Tài liệu gốc: ${material.file.name}`}
          className="h-[50dvh] w-full rounded-2xl bg-slate-100 dark:bg-slate-800"
        >
          <p className="p-4 text-center">
            Trình duyệt này không xem PDF ngay tại đây. Dùng nút bên dưới để mở PDF.
          </p>
        </object>
      )}
      <a
        href={material.url}
        target="_blank"
        rel="noopener noreferrer"
        className="self-start px-1 text-lg text-sky-800 underline dark:text-sky-300"
      >
        {material.kind === 'image' ? 'Xem ảnh cỡ thật' : 'Mở PDF trong thẻ mới'}
      </a>
      {material.kind === 'pdf' && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          Mỗi phần bên cạnh ghi số trang để con tìm trong PDF.
        </p>
      )}
    </section>
  )
}

export function ExtractionReview({
  material,
  review,
  onResolve,
  onConfirm,
  onReject,
  onHome,
}: {
  material: LocalMaterial
  review: ReviewState
  onResolve: (blockId: string, resolution: Resolution) => void
  onConfirm: () => void
  onReject: () => void
  onHome: () => void
}): React.JSX.Element {
  const [highlightedId, setHighlightedId] = useState<string | null>(null)
  const { blocks } = review.extraction
  const lang = review.extraction.subject === 'english' ? 'en' : undefined
  const pending = pendingBlocks(review).length
  const notClear = blocks.filter((b) => b.status !== 'clear').length

  return (
    <JourneyFrame
      step="Nội dung"
      title="Kiểm tra nội dung đọc được"
      onBack={onReject}
      onHome={onHome}
    >
      <p className="text-lg text-slate-700 dark:text-slate-300">
        So sánh từng phần ứng dụng đọc được với tài liệu gốc. Chỉ nội dung con xác nhận
        mới được dùng để học.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Original material={material} blocks={blocks} highlightedId={highlightedId} />
        <section aria-labelledby="real-extracted" className={PANEL}>
          <h2 id="real-extracted" className="text-lg font-semibold">
            <span aria-hidden>🔎 </span>Nội dung đọc được
          </h2>
          <p className="text-base text-slate-700 dark:text-slate-300">
            Đọc bằng {review.extraction.provider.name}. Ứng dụng chỉ đọc chữ, không hiểu
            hay kiểm tra{' '}
            {review.extraction.subject === 'english' ? 'bài làm' : 'bài toán'}. Loại của
            mỗi phần do ứng dụng tự đoán, có thể sai.
          </p>
          {review.extraction.classification === 'uncertain' && (
            <p
              role="note"
              className="rounded-xl border-2 border-amber-400 bg-amber-50 px-3 py-2 text-base font-semibold dark:border-amber-700 dark:bg-amber-950/40"
            >
              {UNCERTAIN_MATERIAL_TEXT}
            </p>
          )}
          {notClear > 0 && (
            <Note tone="info">
              Có {notClear} phần chưa đọc rõ, được tô màu vàng hoặc đỏ.
            </Note>
          )}
          <ol aria-label="Các phần đọc được" className="flex flex-col gap-3">
            {blocks.map((block) => (
              <BlockCard
                key={block.id}
                block={block}
                lang={lang}
                resolution={resolutionOf(review, block.id)}
                highlighted={block.id === highlightedId}
                canLocate={material.kind === 'image' && block.region !== null}
                onLocate={() => {
                  setHighlightedId(block.id)
                }}
                onResolve={(resolution) => {
                  onResolve(block.id, resolution)
                }}
              />
            ))}
          </ol>
        </section>
      </div>

      <p role="status" className="text-lg font-semibold">
        {pending > 0
          ? `Còn ${String(pending)} phần cần con kiểm tra.`
          : canConfirm(review)
            ? 'Con đã kiểm tra xong.'
            : 'Chưa còn phần nào để học. Con giữ lại ít nhất một phần, hoặc chọn tài liệu khác.'}
      </p>
      <Note tone="info">{CONFIRM_MEANING_TEXT}</Note>
      <div className="flex flex-col gap-3 md:flex-row">
        <button
          type="button"
          className={PRIMARY}
          disabled={!canConfirm(review)}
          onClick={onConfirm}
        >
          Xác nhận nội dung
        </button>
        <button type="button" className={SECONDARY} onClick={onReject}>
          Không đúng, chọn tài liệu khác
        </button>
      </div>
    </JourneyFrame>
  )
}

// ---------------------------------------------------------------------------
// Confirmed
// ---------------------------------------------------------------------------

export function ConfirmedContent({
  confirmed,
  onChooseAnother,
  onHome,
  onLearn,
  learnBusy = false,
  learnError = null,
}: {
  confirmed: ConfirmedMaterial
  onChooseAnother: () => void
  onHome: () => void
  /** Start the lesson from this content (D-17); absent where it cannot run. */
  onLearn?: () => void
  learnBusy?: boolean
  learnError?: string | null
}): React.JSX.Element {
  const { source } = confirmed
  const lang = confirmed.subject === 'english' ? 'en' : undefined
  return (
    <JourneyFrame
      step="Nội dung"
      title="Nội dung đã xác nhận"
      onBack={null}
      onHome={onHome}
    >
      <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-amber-100 px-3 py-2 text-base dark:bg-amber-950/40">
        <span aria-hidden>➗</span>
        <span className="min-w-0 flex-1 break-words">
          <span className="font-semibold">Từ tài liệu: </span>
          {source.fileName} · {source.pageCount} trang · {SUBJECT_NAME[confirmed.subject]}
        </span>
      </div>
      <ol aria-label="Nội dung đã xác nhận" className={`${PANEL} list-none`}>
        {confirmed.blocks.map((b) => (
          <li
            key={b.blockId}
            className="flex flex-col gap-1 border-b border-slate-100 pb-2 last:border-0 dark:border-slate-800"
          >
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {KIND_TEXT[b.kind]} · Trang {b.page}
              {b.resolution === 'corrected'
                ? ' · Con đã sửa'
                : b.extractedStatus !== 'clear'
                  ? ' · Con đã kiểm tra'
                  : ''}
            </span>
            <p lang={lang} className="text-lg break-words whitespace-pre-wrap">
              {b.text}
            </p>
          </li>
        ))}
      </ol>
      {confirmed.excluded.length > 0 && (
        <p className="text-base text-slate-700 dark:text-slate-300">
          {confirmed.excluded.length} phần đã bỏ, không dùng để học.
        </p>
      )}
      <Note tone="info">{CONFIRM_MEANING_TEXT}</Note>
      {onLearn === undefined ? (
        <Note tone="info">
          {NEXT_STEP_TEXT} Nội dung này chỉ được giữ trong lúc con đang mở ứng dụng.
        </Note>
      ) : (
        <Note tone="info">
          Khi con bấm “Học từ nội dung này”, phần nội dung con đã xác nhận được lưu để học
          và để bố mẹ xem lại. Tệp gốc không được lưu.
        </Note>
      )}
      {learnError !== null && (
        <p
          role="alert"
          className="rounded-xl bg-rose-50 px-3 py-2 text-lg text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
        >
          {learnError}
        </p>
      )}
      <div className="flex flex-col gap-3 md:flex-row">
        {onLearn !== undefined && (
          <button
            type="button"
            className={PRIMARY}
            disabled={learnBusy}
            onClick={onLearn}
          >
            {learnBusy ? 'Đang chuẩn bị bài học…' : 'Học từ nội dung này'}
          </button>
        )}
        <button
          type="button"
          className={onLearn === undefined ? PRIMARY : SECONDARY}
          onClick={onHome}
        >
          Về trang chính
        </button>
        <button type="button" className={SECONDARY} onClick={onChooseAnother}>
          Chọn tài liệu khác
        </button>
      </div>
    </JourneyFrame>
  )
}
